<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePaymentStatus } from 'dashboard/composables/usePaymentStatus';
import { format } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';

import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import Banner from 'next/banner/Banner.vue';
import { getCurrencyConfig } from 'dashboard/constants/billing';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { currentAccount, isOnChatwootCloud } = useAccount();
const { isPastDue } = usePaymentStatus();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();
const { t } = useI18n();

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

// State for handling refresh attempts and loading
const isWaitingForBilling = ref(false);

// Currency selection shown to new accounts whose locale supports a non-USD currency.
const currencySelectionRequired = ref(false);
const currencyOptions = ref([]);

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const billingCurrency = computed(() => {
  if (!customAttributes.value.billing_currency) return '';
  return t(
    getCurrencyConfig(customAttributes.value.billing_currency).i18nLabelKey
  );
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

// Set only while a cancellation is scheduled; the plan stays active until this date.
const subscriptionCancelsOn = computed(() => {
  if (!customAttributes.value.subscription_cancels_on) return '';
  const cancelDate = new Date(customAttributes.value.subscription_cancels_on);
  return format(cancelDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    const data = await store.dispatch('accounts/subscription');
    currencySelectionRequired.value = !!data?.currency_selection_required;
    currencyOptions.value = data?.currency_options || [];
  }
};

const handleBillingPageLogic = async () => {
  // If self-hosted, redirect to dashboard
  if (!isOnChatwootCloud.value) {
    router.push({ name: 'home' });
    return;
  }

  // Check if we've already attempted a refresh for billing setup
  const billingRefreshAttempted = sessionStorage.get(BILLING_REFRESH_ATTEMPTED);

  // If cloud user, fetch account details first
  await fetchAccountDetails();

  // Waiting on the user to pick a billing currency — don't auto-refresh.
  if (currencySelectionRequired.value) return;

  // If still no billing plan after fetch
  if (!hasABillingPlan.value) {
    // If we haven't attempted refresh yet, do it once
    if (!billingRefreshAttempted) {
      isWaitingForBilling.value = true;
      sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);

      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else {
      // We've already tried refreshing, so just show the no billing message
      // Clear the flag for future visits
      sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    }
  } else {
    // Billing plan found, clear any existing refresh flag
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
  }
};

const onSelectCurrency = async code => {
  await store.dispatch('accounts/selectBillingCurrency', code);
  currencySelectionRequired.value = false;
  // Currency stored and customer creation kicked off — resume the standard wait flow.
  await handleBillingPageLogic();
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

onMounted(handleBillingPageLogic);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem || isWaitingForBilling"
    :loading-message="
      isWaitingForBilling
        ? $t('BILLING_SETTINGS.NO_BILLING_USER')
        : $t('ATTRIBUTES_MGMT.LOADING')
    "
    :no-records-found="
      !hasABillingPlan && !isWaitingForBilling && !currencySelectionRequired
    "
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section v-if="currencySelectionRequired" class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.CURRENCY.SELECT.TITLE')"
          :description="$t('BILLING_SETTINGS.CURRENCY.SELECT.DESCRIPTION')"
        >
          <template #action>
            <div class="flex gap-2">
              <ButtonV4
                v-for="code in currencyOptions"
                :key="code"
                sm
                solid
                blue
                :is-loading="uiFlags.isCheckoutInProcess"
                :disabled="uiFlags.isCheckoutInProcess"
                @click="onSelectCurrency(code)"
              >
                {{ $t(getCurrencyConfig(code).i18nLabelKey) }}
              </ButtonV4>
            </div>
          </template>
        </BillingCard>
      </section>
      <section v-else class="grid gap-4">
        <Banner
          v-if="isPastDue"
          color="ruby"
          role="alert"
          class="flex-wrap"
          :action-label="$t('BILLING_SETTINGS.PAYMENT_RECOVERY.ACTION')"
          :is-loading="uiFlags.isCheckoutInProcess"
          @action="onClickBillingPortal"
        >
          {{ $t('BILLING_SETTINGS.PAYMENT_RECOVERY.DESCRIPTION') }}
        </Banner>
        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 sm solid blue @click="onClickBillingPortal">
              {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </template>
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionCancelsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.CANCELS_ON')"
              :value="subscriptionCancelsOn"
            />
            <DetailItem
              v-else-if="subscriptionRenewsOn && !isPastDue"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
            <DetailItem
              v-if="billingCurrency"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.CURRENCY')"
              :value="billingCurrency"
            />
          </div>
        </BillingCard>

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
    </template>
  </SettingsLayout>
</template>
